import React, { useEffect, } from 'react'
import { Outlet, useSearchParams, useLocation } from 'react-router-dom'

import useTranslate from "../../../hooks/useTranslate";
import { makeUrl } from '../../../api/makeUrl'
import useAuthenticationStore, { AuthenticationViews } from './Store'
import './auth-design-system-vars.css'
import './authentication.css'
import LoginHeader from './LoginHeader';
import LoginFooter from './LoginFooter';
import { useConfiguration } from './Configuration';
import { getIdentifier, getLinkKey, getProjectCode, isValidEmailOrPhone, isValidNonce, isValidGuid } from '../utils';
import CookiesBanner from './CookiesBanner';

export const ProductNames = {
  MyDataHelps: 'MyDataHelps',
  MyFHR: 'My Family Health Record',
}

const Authentication: React.FC = () => {
  const { state, dispatch, validateIdentifier, verifyEmail, verifyEmailUpdate, setActiveView, verifyPasswordResetNonce, checkLogin, loadProject, trackRegistrationEvent, getLoginCookie, setRememberMe, changeLanguage } = useAuthenticationStore()
  const { activeView, redirectUrl, currentLanguage, project } = state;
  const { configuration } = useConfiguration()
  const { productName, userType } = configuration;
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const translations = useTranslate(["try-mdhd-url"]);

  const getProjectImageUrl = () => {
    if (!project) { return; }
    return makeUrl("api/researchkit/organizationbrandingimage/" + project.OrganizationID);
  }

  // Initial load effects
  useEffect(() => {
    const emailParam = searchParams.get("email")
    const phoneParam = searchParams.get("phone")
    const nonceParam = searchParams.get("nonce")
    const clientStateIdParam = searchParams.get("clientStateID")
    const knownUserEmailOrPhone = getLoginCookie()

    const emailOrPhone = (emailParam && isValidEmailOrPhone(emailParam)) ? emailParam : (phoneParam && isValidEmailOrPhone(phoneParam)) ? phoneParam : knownUserEmailOrPhone;

    if (emailOrPhone) {
      dispatch({
        type: 'SET_EMAIL_OR_PHONE',
        payload: emailOrPhone
      });
    }

    if (knownUserEmailOrPhone) {
      setRememberMe(true);
    }

    if (nonceParam && isValidNonce(nonceParam)) {
      dispatch({ type: 'SET_VERIFICATION_CODE', payload: nonceParam });
    }

    if (location.pathname === "/verify-email" && (emailParam || knownUserEmailOrPhone) && nonceParam) {
      const verifiedEmailOrPhone = (emailParam && isValidEmailOrPhone(emailParam)) ? emailParam : knownUserEmailOrPhone;
      if (verifiedEmailOrPhone && isValidNonce(nonceParam)) {
        verifyEmail(verifiedEmailOrPhone, nonceParam, searchParams.get("redirectToApp") == "True" ? configuration.nativeAppRedirectUrl : '');
      }
    } else if (location.pathname === "/verify-email-update" && clientStateIdParam && isValidGuid(clientStateIdParam)) {
      verifyEmailUpdate(clientStateIdParam);
    } else if (location.pathname === "/reset-password" && nonceParam && (emailParam || phoneParam)) {
      const verifiedEmailOrPhone = (emailParam && isValidEmailOrPhone(emailParam)) ? emailParam : (phoneParam && isValidEmailOrPhone(phoneParam)) ? phoneParam : undefined;
      if (verifiedEmailOrPhone && isValidNonce(nonceParam)) {
        setActiveView(AuthenticationViews.verifyNonce)
        verifyPasswordResetNonce(verifiedEmailOrPhone, nonceParam);
      }
    } else if (configuration.lastIdp) {
      setActiveView(AuthenticationViews.externalLogin)
    } else if (knownUserEmailOrPhone) {
      setActiveView(AuthenticationViews.knownUserLogin)
    }
  }, [])

  // Update the active view when the URL changes
  useEffect(() => {
    if (!location.pathname) {
      dispatch({ type: "SET_ACTIVE_VIEW", payload: undefined });
      return;
    };
    let newLocation = location.pathname.substring(1) as AuthenticationViews;
    dispatch({ type: "SET_ACTIVE_VIEW", payload: newLocation });
  }, [location]);

  // Changes when the active view changes
  useEffect(() => {
    if (!state.activeView) return;
    // wait for project to be loaded before doing any redirects
    if ((getProjectCode() || getLinkKey()) && !state.project) { return; }

    if (configuration.hasIdentityServerLogin && (
      state.activeView === AuthenticationViews.splash ||
      state.activeView === AuthenticationViews.join ||
      state.activeView === AuthenticationViews.login ||
      state.activeView === AuthenticationViews.knownUserLogin
    ) && (!state.project || state.project?.Enrolled)) { // don't automatically redirect if user is not enrolled in the project
      window.location.href = makeUrl(state.redirectUrl);
      return;
    }

    //return to join if user goes back from set password to verify phone
    if (state.activeView == AuthenticationViews.verifyPhone && state.verifiedPhoneToken) {
      setActiveView(AuthenticationViews.join);
      return;
    }
    if (state.project && state.project.RequiresIdentifierValidation && state.activeView === AuthenticationViews.join && !state.identifierNonce) {
      setActiveView(AuthenticationViews.identifierCheck);
      return;
    }

    trackRegistrationEvent("Page Viewed");
    dispatch({
      type: 'SET_SERVER_ERROR', payload: ''
    })

    if (state.project && !state.project.SupportedLocales.includes(state.currentLanguage)) {
      changeLanguage(state.project.DefaultLocale);
    }
  }, [state.activeView, state.project])

  useEffect(() => {
    async function preparePage() {
      const identifier = getIdentifier()
      let newRedirectUrl = redirectUrl
      if (identifier) {
        newRedirectUrl = await validateIdentifier(identifier, redirectUrl)
      }

      newRedirectUrl = await loadProject(newRedirectUrl);
      if (newRedirectUrl) {
        dispatch({ type: 'SET_LOGIN_CHECK_TIMER', payload: window.setInterval(checkLogin, 1500) });
      }
    }
    preparePage()
  }, [])

  return (
    <div id="authentication" className={`${project ? 'white-labelled' : 'unbranded'} ${productName === ProductNames.MyFHR ? 'product-my-fhr' : ''} user-${userType.toLowerCase()}`}>
      <LoginHeader productName={productName} />
      <main className="simple-layout">
        <section className='authentication-action'>
          {
            productName == ProductNames.MyDataHelps && (
              <div className={`header ${[AuthenticationViews.setPassword, AuthenticationViews.verifyPhone, AuthenticationViews.loginPassword].includes(activeView!) ? 'mobile-hide' : ''} ${project ? 'project-selected' : ''}`}>
                {
                  project ? (
                    <div className="project">
                      <div className="project-header">
                        <div className="project-logo">
                          <img src={getProjectImageUrl()} />
                        </div>
                        <div className="project-information">
                          <h3 className="project-name">{project.Name}</h3>
                          <div className="project-organization">{project.OrganizationName}</div>
                        </div>
                        {
                          (activeView === AuthenticationViews.splash && project.Description) &&
                          <div className="project-description">{project.Description}</div>
                        }
                      </div>
                    </div>
                  ) : (
                    ''
                  )
                }
              </div>
            )}
          <div className='authentication-action-content'>
            <Outlet />
          </div>
        </section >
        {
          productName === ProductNames.MyDataHelps && !productName && (
            <header className="logo-header-mobile">
              <div className="logo-header-mobile-image-wrap">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/logo_app_icon.svg" alt=" My Data Helps" className="logo-app-icon" />
              </div>
            </header>
          )
        }
      </main >
      <LoginFooter />
      <CookiesBanner />
    </div >
  )
}

export default Authentication;